import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { Vault, Loader2, XCircle, BadgeCheck, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUSD = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtTs = (s) => { try { return s ? new Date(s).toUTCString() : "—"; } catch { return s || "—"; } };

const Row = ({ label, value, mono }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2.5 border-b border-[#2A2A22]">
    <div className="sm:w-52 shrink-0 text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A]">{label}</div>
    <div className={`text-[13px] text-[#ECECE4] ${mono ? "font-mono text-[11px] break-all" : ""}`}>{value}</div>
  </div>
);

export default function RedeemDraft() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const fetched = useRef(false);

  useEffect(() => {
    // Guard against React.StrictMode double-invoking the effect in dev/preview
    // builds: the redeem endpoint is idempotent, so a duplicate call would
    // return already_redeemed=true and clobber the fresh "Withdrawal Confirmed".
    if (fetched.current) return;
    fetched.current = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/vault/redeem/${token}`);
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "This redemption code is invalid or could not be processed.");
      }
    })();
  }, [token]);

  const already = data?.already_redeemed;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4] px-4 py-10" data-testid="redeem-draft-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="text-[12px] text-[#8A8A8A] hover:text-[#ECECE4]" data-testid="redeem-home-link">← Return to Home</Link>

        <div className="mt-5 bg-[#13130E] border border-[#2A2A22] rounded-xl overflow-hidden">
          <div className="px-7 py-6 border-b border-[#2A2A22] flex items-center gap-3">
            <Vault size={22} className="text-[#0B6B4F]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A]">Draft Vault · Electronic Withdrawal</div>
              <div className="text-xl font-serif">Withdraw / Redeem Confirmation</div>
            </div>
          </div>

          {error ? (
            <div className="px-7 py-12 text-center" data-testid="redeem-error">
              <XCircle size={34} className="text-[#FF8585] mx-auto mb-3" />
              <div className="text-[#FF8585] text-sm">{error}</div>
            </div>
          ) : !data ? (
            <div className="px-7 py-16 text-center" data-testid="redeem-loading">
              <Loader2 className="animate-spin inline text-[#0B6B4F]" />
              <div className="mt-3 text-[12px] text-[#8A8A8A]">Processing withdrawal from vault…</div>
            </div>
          ) : (
            <div className="px-7 py-6" data-testid="redeem-result">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 mb-5 text-[11px] uppercase tracking-[0.14em] font-bold rounded-full ${already ? "bg-[#B8860B]/15 text-[#E4C97A] border border-[#B8860B]/50" : "bg-[#0B6B4F]/15 text-[#3DBB8E] border border-[#0B6B4F]/50"}`} data-testid="redeem-status-badge">
                <BadgeCheck size={14} /> {already ? "Already Withdrawn" : "Withdrawal Confirmed"}
              </div>
              <Row label="Instrument" value={`Private Negotiable Instrument No. ${data.draft_no || "—"}`} />
              <Row label="Face Amount" value={<span className="font-semibold text-[#3DBB8E]">{fmtUSD(data.amount)}</span>} />
              <Row label="Drawer / Maker" value={data.drawer_name || "—"} />
              <Row label="Payee / Counterparty" value={data.pay_to || "—"} />
              <Row label="Maturity" value={data.maturity_date || "—"} />
              <Row label="Vault ID" value={data.vault_id} mono />
              <Row label="Acceptance ID" value={data.acceptance_id} mono />
              <Row label="Status" value={<span className="uppercase">{data.status}</span>} />
              <Row label="Instrument SHA-256" value={data.sha256 || "—"} mono />
              {data.reserve_backing?.backed && (
                <Row label="Reserve Backing" value={
                  <span className="text-[#3DBB8E]" data-testid="redeem-reserve-backing">✓ Reserve-Backed · {data.reserve_backing.reserve_no} · ratio {(data.reserve_backing.ratio * 100).toFixed(0)}%</span>
                } />
              )}
              <p className="mt-5 text-[11px] text-[#5C5C5C] leading-relaxed flex items-start gap-2">
                <ShieldCheck size={14} className="text-[#0B6B4F] shrink-0 mt-0.5" />
                {already
                  ? "This instrument was previously withdrawn from the vault. The record above is provided for your reference; scanning again does not create a duplicate withdrawal."
                  : "This confirms the instrument has been electronically withdrawn (deposited) from the Draft Vault and recorded in the Acceptance Inbox. Retain this reference for your records."}
              </p>
              <p className="mt-2 text-[10px] text-[#4C4C46] leading-relaxed" data-testid="redeem-endorsement">
                Endorsement / transfer of this instrument upon withdrawal is made <span className="text-[#8A8A8A] font-semibold">WITHOUT RECOURSE</span> to the transferor (UCC §3-415(b)); the maker's / acceptor's underlying promise or order to pay remains <span className="text-[#8A8A8A] font-semibold">unconditional</span> (UCC §3-104).
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
